/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import SearchIcon from '@mui/icons-material/Search';
import { Box, FormControl, InputAdornment, InputLabel, OutlinedInput } from '@mui/material';

import { SERVICE_TYPES } from 'shared';

import { ArrowRoundedIcon } from '../../../../components/svg-icons/ArrowRoundedIcon';
import { HUAutocomplete } from '../../../../elements/HUAutocomplete/HUAutocomplete';
import { destructRenderInputParams } from '../../../../elements/HUTextInput/utils';
import { Namespaces } from '../../../../i18n';
import { ClusterStatus } from '../../../../types/cluster/ClusterStatus';
import { IOption } from '../ProjectPage.types';
import { createFilterPanelStyles } from './FilterPanel.styles';

export type FilterPanelProps = {
  isLoading: boolean;
  search: string;
  type: IOption | null;
  status: IOption | null;
  instance: IOption | null;
  selectInstanceOptions: IOption[];
  onChangeSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeSelect: (name: string, value: IOption | null) => any;
  disabled: boolean;
};

export const FilterPanel = memo<FilterPanelProps>(props => {
  const { t } = useTranslation(Namespaces.Cluster);
  const classes = createFilterPanelStyles();
  const { isLoading, search, type, status, instance, selectInstanceOptions, onChangeSearch, onChangeSelect, disabled } =
    props;

  const typeOptions = useMemo<IOption[]>(
    () => SERVICE_TYPES.map(v => ({ text: t(`general:services.types.${v}.short`), value: v })),
    [t],
  );

  const statusOptions: IOption[] = [
    { value: ClusterStatus.running },
    { value: ClusterStatus.updating },
    { value: ClusterStatus.creating },
    { value: ClusterStatus.unavailable },
    { value: ClusterStatus.error },
  ];

  return (
    <Box className={classes.root} maxWidth="714px">
      <div className={classes.itemsWrapper}>
        <div className={classes.filterItem}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel htmlFor="search">{t('search')}</InputLabel>
            <OutlinedInput
              id="search"
              name="search"
              className={cx(classes.search)}
              value={search}
              fullWidth
              label={t('search')}
              onChange={onChangeSearch}
              disabled={isLoading || disabled}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
            />
          </FormControl>
        </div>
        <div className={cx(classes.filterItem)}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel htmlFor="type">{t('type')}</InputLabel>
            <HUAutocomplete
              popupIcon={<ArrowRoundedIcon />}
              renderInput={renderParams => (
                <OutlinedInput {...destructRenderInputParams(renderParams)} name="type" label={t('type')} fullWidth />
              )}
              options={typeOptions}
              value={type}
              onChange={(_e: any, value: IOption | null) => {
                onChangeSelect('type', value);
              }}
              getOptionLabel={v => `${v.text || v.value}`}
              openOnFocus={true}
            />
          </FormControl>
        </div>
        <div className={cx(classes.filterItem)}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel htmlFor="instance">{t('overview.table.instances')}</InputLabel>
            <HUAutocomplete
              popupIcon={<ArrowRoundedIcon />}
              renderInput={renderParams => (
                <OutlinedInput
                  {...destructRenderInputParams(renderParams)}
                  name="instance"
                  label={t('overview.table.instances')}
                  fullWidth
                />
              )}
              options={selectInstanceOptions}
              value={instance}
              onChange={(_e: any, value: IOption | null) => {
                onChangeSelect('instance', value);
              }}
              getOptionLabel={v => `${v.text || v.value}`}
              openOnFocus={true}
            />
          </FormControl>
        </div>

        <div className={cx(classes.filterItem)}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel htmlFor="status">{t('status')}</InputLabel>
            <HUAutocomplete
              popupIcon={<ArrowRoundedIcon />}
              renderInput={renderParams => (
                <OutlinedInput
                  {...destructRenderInputParams(renderParams)}
                  name="status"
                  label={t('status')}
                  fullWidth
                />
              )}
              options={statusOptions}
              value={status}
              onChange={(_e: any, value: IOption | null) => {
                onChangeSelect('status', value);
              }}
              getOptionLabel={v => `${v.text || v.value}`}
              openOnFocus={true}
            />
          </FormControl>
        </div>
      </div>
    </Box>
  );
});
